import { controllerBoardLayout } from "./ControllerBoardModel";
import { hardwareProducts } from "./hardwareShowcaseProducts";
import { bindShowcaseMotion, disposeShowcaseModel } from "./hardwareShowcaseModel";

// The published presentation GLB uses metres: KiCad X−63 mm, Y−153 mm and
// the main PCB middle plane at 19.438 mm. This rigid mapping preserves the
// existing Twin board coordinate frame and never changes the fabrication CAD.
export const controllerEnclosureLayout = Object.freeze({
  asset: hardwareProducts.aeroponic.url,
  sourceRevision: "Rev31-12 + enclosure v14",
  cadCentre: Object.freeze([.08731505, .019438, -.0452]),
  metresToDrawing: 1000 * controllerBoardLayout.drawingWidth / (controllerBoardLayout.maxX - controllerBoardLayout.minX),
  rotationX: Math.PI / 2,
  maxBytes: 10 * 1024 * 1024,
});

// These are connector-mouth / gland-exit drawing anchors, not electrical pins.
// The values are from the already-published Rev31/v14 render mesh bounds. X2
// uses gland 1, aligned with its +24V/GND pair; X1 uses gland 2 for MOTOR A/B.
const cadCableEntries = Object.freeze({
  J2: [.09175, .023411, -.00925], J16: [.11595, .023411, -.00925],
  J4: [.14015, .023411, -.00930], J6: [.15808, .021441, -.01245],
  J17: [.137, .023011, -.0774], J7: [.1475, .023011, -.0774], J8: [.1575, .023011, -.0774],
  X1: [.05875, .033, .0136], X2: [.0378, .033, .0136],
});
export function controllerEnclosurePoint(THREE, point) {
  const [cx, cy, cz] = controllerEnclosureLayout.cadCentre;
  return new THREE.Vector3(point[0] - cx, -(point[2] - cz), point[1] - cy).multiplyScalar(controllerEnclosureLayout.metresToDrawing);
}
export function controllerEnclosureCableEndpoints(THREE) {
  return new Map(Object.entries(cadCableEntries).map(([ref, point]) => [ref, controllerEnclosurePoint(THREE, point)]));
}
export function controllerEnclosureCableDirections(THREE) {
  return new Map(Object.keys(cadCableEntries).map(ref => [ref, new THREE.Vector3(0, ["J7", "J8", "J17"].includes(ref) ? 1 : -1, 0)]));
}

const one = (root, name) => {
  const values = []; root.traverse(object => { if (object.name === name) values.push(object); });
  if (values.length !== 1) throw new Error(`Controller CAD requires one ${name}`);
  return values[0];
};
const pose = object => ({ parent: object.parent, position: object.position.clone(), quaternion: object.quaternion.clone(), scale: object.scale.clone() });
const restorePose = (object, previous) => {
  object.removeFromParent(); previous.parent?.add(object);
  object.position.copy(previous.position); object.quaternion.copy(previous.quaternion); object.scale.copy(previous.scale);
};

/** Takes ownership of model, but borrows the caller's stable display interface. */
export function createControllerEnclosureAssembly(THREE, model, { displaySurface } = {}) {
  let adopted = false, disposed = false, dynamicGeometry = null, root = null;
  const originalRoot = displaySurface?.root && pose(displaySurface.root);
  const originalSurface = displaySurface?.surface && pose(displaySurface.surface);
  const originalGeometry = displaySurface?.surface?.geometry;
  const release = () => {
    if (disposed) return;
    disposed = true;
    if (adopted) {
      // Remove borrowed resources before traversing/discarding the CAD model.
      restorePose(displaySurface.surface, originalSurface);
      displaySurface.surface.geometry = originalGeometry;
      restorePose(displaySurface.root, originalRoot);
    }
    dynamicGeometry?.dispose(); dynamicGeometry = null;
    disposeShowcaseModel(model); root?.clear();
  };
  try {
    if (!model?.isObject3D || !displaySurface?.root?.isObject3D || !displaySurface?.surface?.isMesh
        || !displaySurface.surface.geometry?.isBufferGeometry || !displaySurface.surface.material?.isMeshBasicMaterial) throw new Error("Controller CAD requires the original display interface");
    const mainBoard = one(model, "board_main"), gateway = one(model, "board_daughter");
    const screen = one(model, hardwareProducts.aeroponic.displaySurfaceName), display = one(model, "display");
    const shell = one(model, "shell_front"), chassis = one(model, "chassis");
    if (mainBoard.isMesh || gateway.isMesh || screen.parent !== display || display.parent !== shell
        || !screen.isMesh || !screen.material?.isMeshBasicMaterial || !screen.material.map?.isTexture
        || screen.geometry.attributes.position?.count !== 4 || screen.geometry.attributes.uv?.count !== 4) throw new Error("Invalid complete controller/display CAD hierarchy");
    const boards = [mainBoard, gateway];
    const motion = bindShowcaseMotion(THREE, model, hardwareProducts.aeroponic.movingParts);
    Object.keys(hardwareProducts.aeroponic.movingParts).forEach(name => {
      const owner = one(model, name);
      boards.forEach(board => {
        for (let current = board; current; current = current.parent) if (current === owner) throw new Error("PCB must not move with enclosure shells");
      });
    });
    const ports = new Map();
    ["J2", "J16", "J4", "J6", "J17", "J7", "J8"].forEach(ref => {
      const value = one(mainBoard, ref); value.userData.controllerPort = ref; ports.set(ref, value);
    });
    [["X2", "GLAND_1_DIMENSION_ENVELOPE"], ["X1", "GLAND_2_DIMENSION_ENVELOPE"]].forEach(([ref, name]) => {
      const value = one(chassis, name); value.userData.controllerPort = ref; ports.set(ref, value);
    });
    root = new THREE.Group(); root.name = "controller-complete-cad-assembly";
    root.userData = { partId: "controller", source: controllerEnclosureLayout.asset, sourceRevision: controllerEnclosureLayout.sourceRevision, presentationOnly: true };
    const rotation = new THREE.Group(); rotation.name = "controller-cad-metre-to-board-frame";
    rotation.rotation.x = controllerEnclosureLayout.rotationX;
    rotation.scale.setScalar(controllerEnclosureLayout.metresToDrawing);
    model.position.sub(new THREE.Vector3(...controllerEnclosureLayout.cadCentre));
    rotation.add(model); root.add(rotation); root.updateMatrixWorld(true);
    const bounds = new THREE.Box3().setFromObject(root), size = bounds.getSize(new THREE.Vector3());
    if (![size.x, size.y, size.z].every(Number.isFinite) || size.x < 1.8 || size.x > 2.6 || size.y < .8 || size.y > 1.5 || size.z < .4 || size.z > .9) throw new Error("Controller CAD envelope mismatch");

    model.traverse(object => {
      if (object.isMesh) { object.castShadow = object !== screen; object.receiveShadow = object !== screen; }
    });
    mainBoard.userData.partId = "controller"; gateway.userData.partId = "gateway";
    dynamicGeometry = screen.geometry.clone();
    // GLB/image convention has v=0 at the top. The existing RGB24/screenshot
    // surface contract and hit mapping use v=1 at the top; retain that contract.
    const uv = dynamicGeometry.getAttribute("uv");
    for (let i = 0; i < uv.count; i += 1) uv.setY(i, 1 - uv.getY(i));
    uv.needsUpdate = true;
    screen.visible = false;
    display.add(displaySurface.root);
    displaySurface.root.position.copy(screen.position);
    displaySurface.root.quaternion.copy(screen.quaternion);
    displaySurface.root.scale.copy(screen.scale);
    displaySurface.surface.position.set(0, 0, 0);
    displaySurface.surface.quaternion.identity(); displaySurface.surface.scale.set(1, 1, 1);
    displaySurface.surface.geometry = dynamicGeometry;
    adopted = true;
    root.updateMatrixWorld(true);
    const staticBoardTransforms = boards.map(board => ({ board, position: board.position.clone(), quaternion: board.quaternion.clone(), scale: board.scale.clone() }));
    return {
      root, model, mainBoard, gateway, display, displaySurface, ports, bounds,
      portEndpoints: controllerEnclosureCableEndpoints(THREE), portDirections: controllerEnclosureCableDirections(THREE),
      setExploded(value) {
        if (disposed) return;
        motion.apply(value === true ? 1 : 0);
        // Board nodes and their entire populated subtrees remain assembled.
        staticBoardTransforms.forEach(({ board, position, quaternion, scale }) => {
          board.position.copy(position); board.quaternion.copy(quaternion); board.scale.copy(scale);
        });
        root.updateMatrixWorld(true);
      },
      dispose: release,
    };
  } catch (error) { release(); throw error; }
}

const abortError = () => new DOMException("Controller CAD request aborted", "AbortError");
function localAssetUrl(value) {
  const base = typeof window !== "undefined" ? window.location.href : "http://localhost/";
  const url = new URL(value, base);
  if (typeof value !== "string" || url.origin !== new URL(base).origin || !url.pathname.endsWith(controllerEnclosureLayout.asset)
      || url.username || url.password || url.search || url.hash) throw new Error("Controller CAD requires the local published assembly");
  return url.href;
}
function validateEmbeddedGlb(bytes) {
  if (!(bytes instanceof ArrayBuffer) || bytes.byteLength < 28 || bytes.byteLength > controllerEnclosureLayout.maxBytes) throw new Error("Invalid controller CAD byte size");
  const view = new DataView(bytes);
  if (view.getUint32(0, true) !== 0x46546c67 || view.getUint32(4, true) !== 2 || view.getUint32(8, true) !== bytes.byteLength
      || view.getUint32(16, true) !== 0x4e4f534a) throw new Error("Invalid controller GLB container");
  const length = view.getUint32(12, true);
  if (length < 2 || length > 1024 * 1024 || 20 + length > bytes.byteLength) throw new Error("Invalid controller GLB metadata");
  const json = JSON.parse(new TextDecoder().decode(new Uint8Array(bytes, 20, length)));
  if ([...(json.buffers || []), ...(json.images || [])].some(item => item.uri)
      || (json.extensionsRequired || []).some(name => !["EXT_meshopt_compression", "KHR_mesh_quantization", "KHR_materials_unlit"].includes(name))) throw new Error("Controller CAD must be self-contained");
}
async function parseControllerGlb(bytes) {
  const [{ GLTFLoader }, { MeshoptDecoder }] = await Promise.all([
    import("three/examples/jsm/loaders/GLTFLoader.js"),
    import("three/examples/jsm/libs/meshopt_decoder.module.js"),
  ]);
  return new GLTFLoader().setMeshoptDecoder(MeshoptDecoder).parseAsync(bytes, "");
}

/** No simulator launch, API call or hardware command: one bounded public asset. */
export async function loadControllerEnclosureAssembly(THREE, {
  signal, displaySurface, url = controllerEnclosureLayout.asset, fetcher = fetch, parse = parseControllerGlb,
} = {}) {
  if (!signal || typeof signal.addEventListener !== "function") throw new TypeError("Controller CAD requires an abort signal");
  if (signal.aborted) throw abortError();
  const response = await fetcher(localAssetUrl(url), { signal, credentials: "omit" });
  if (signal.aborted) throw abortError();
  if (!response.ok) throw new Error("Controller CAD unavailable");
  const advertisedSize = Number(response.headers?.get("content-length"));
  if (Number.isFinite(advertisedSize) && advertisedSize > controllerEnclosureLayout.maxBytes) throw new Error("Controller CAD exceeds size limit");
  const bytes = await response.arrayBuffer();
  if (signal.aborted) throw abortError();
  validateEmbeddedGlb(bytes);
  const gltf = await new Promise((resolve, reject) => {
    let aborted = false;
    const cancel = () => { aborted = true; signal.removeEventListener("abort", cancel); reject(abortError()); };
    signal.addEventListener("abort", cancel, { once: true });
    if (signal.aborted) { cancel(); return; }
    Promise.resolve().then(() => parse(bytes)).then(value => {
      signal.removeEventListener("abort", cancel);
      if (aborted || signal.aborted) { disposeShowcaseModel(value?.scene); reject(abortError()); return; }
      resolve(value);
    }, error => { signal.removeEventListener("abort", cancel); reject(error); });
  });
  if (signal.aborted) { disposeShowcaseModel(gltf?.scene); throw abortError(); }
  return createControllerEnclosureAssembly(THREE, gltf?.scene, { displaySurface });
}
